import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sprout,
  MapPin,
  FileText,
  Package,
  BarChart3,
  MessageSquare,
  LogOut,
  Send,
  Loader2,
  Sparkles,
  ShieldCheck,
  TrendingUp,
  Calendar,
  Camera,
  Plus,
  Pencil,
  Pen,
  Eye,
  CheckCircle,
  Euro,
  X,
  Check,
  Map as MapIcon,
  LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from 'recharts';
import MBAPubCarousel from '../components/MBAPubCarousel';
import ParcellesMap from '../components/ParcellesMap';
import SignaturePadModal from '../components/SignaturePadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#84cc16', '#10b981', '#06b6d4', '#6366f1', '#f59e0b', '#ef4444'];

export default function PortailClientETAPage() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState('stats');
  const [parcelles, setParcelles] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [bons, setBons] = useState([]);
  const [produitsUtilises, setProduitsUtilises] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      text: "Bonjour ! Je suis l'assistant IA de votre ETA. Posez-moi une question sur vos parcelles, interventions ou produits — je ne peux rien modifier, mais je peux tout vous expliquer.",
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [teaser, setTeaser] = useState(null);
  const chatEndRef = useRef(null);
  const [demandes, setDemandes] = useState([]);
  const [demandeForm, setDemandeForm] = useState(null); // null | objet ouvert
  const [valentinePendingCount, setValentinePendingCount] = useState(0);
  const [documentsASigner, setDocumentsASigner] = useState(0);
  const [documentsTous, setDocumentsTous] = useState({ documents: [], stats: {} });

  const token = localStorage.getItem('portail_eta_token');

  useEffect(() => {
    if (!token) {
      navigate('/portail-eta/login');
      return;
    }
    const c = JSON.parse(localStorage.getItem('portail_eta_client') || 'null');
    setClient(c);
    loadAll();
  }, []); // eslint-disable-line

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Valentine proactive : au chargement, on demande s'il y a une question à poser
  //   + polling toutes les 60s pour détecter de nouvelles questions
  //   + notification navigateur si permission accordée
  useEffect(() => {
    if (!token || !client) return;

    // Demander permission notification navigateur (silencieux si déjà décidé)
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (_) { /* silent */ }
    }

    const seenKeys = new Set();

    const fetchCount = async () => {
      try {
        const c = await axios.get(`${API}/portail-eta/ai/valentine/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setValentinePendingCount(c.data?.count || 0);
      } catch (_) { /* silent */ }
    };

    const fetchAndNotify = async (isFirst = false) => {
      try {
        const r = await axios.get(`${API}/portail-eta/ai/valentine/prochaine-question`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await fetchCount();
        if (!r.data?.has_question || !r.data.question?.question) return;
        const q = r.data.question;
        if (seenKeys.has(q.key)) return;
        seenKeys.add(q.key);

        setChatMessages((m) => [
          ...m,
          {
            role: 'assistant',
            text: q.question,
            proactive: true,
            question_key: q.key,
          },
        ]);

        if (!isFirst) {
          toast.info("Valentine a une précision à vous demander (30 sec chrono)", {
            duration: 6000,
          });
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              const n = new Notification("Valentine — Bureau Agricole", {
                body: "Une précision technique est demandée pour finaliser votre dossier.",
                icon: '/favicon.ico',
                tag: `valentine-${q.key}`,
                silent: false,
              });
              n.onclick = () => { window.focus(); };
            }
          } catch (_) { /* silent */ }
        }
      } catch (_) { /* silent */ }
    };

    // Premier appel immédiat (sans toast/notif)
    fetchAndNotify(true);
    // Polling toutes les 60s
    const intervalId = setInterval(() => fetchAndNotify(false), 60000);
    return () => clearInterval(intervalId);
  }, [client, token]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchDocumentsTous = async () => {
    try {
      const r = await axios.get(`${API}/portail-eta/documents-tous`, auth);
      setDocumentsTous(r.data);
      setDocumentsASigner(r.data?.stats?.a_signer || 0);
    } catch (_) { /* silent */ }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pRes, iRes, bRes, puRes, cRes, sRes, phRes, dRes, docsRes] = await Promise.all([
        axios.get(`${API}/portail-eta/parcelles`, auth),
        axios.get(`${API}/portail-eta/interventions?limit=100`, auth),
        axios.get(`${API}/portail-eta/bons-chantier`, auth),
        axios.get(`${API}/portail-eta/produits-utilises`, auth),
        axios.get(`${API}/portail-eta/produits-catalogue`, auth),
        axios.get(`${API}/portail-eta/stats`, auth),
        axios.get(`${API}/portail-eta/photos?limit=200`, auth),
        axios.get(`${API}/portail-eta/demandes-intervention`, auth),
        axios.get(`${API}/portail-eta/documents-tous`, auth),
      ]);
      setParcelles(pRes.data.parcelles || []);
      setInterventions(iRes.data.interventions || []);
      setBons(bRes.data.bons || []);
      setProduitsUtilises(puRes.data.produits || []);
      setCatalogue(cRes.data.catalogue || []);
      setStats(sRes.data);
      setPhotos(phRes.data.photos || []);
      setDemandes(dRes.data.demandes || []);
      setDocumentsTous(docsRes.data);
      setDocumentsASigner(docsRes.data?.stats?.a_signer || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('portail_eta_token');
    localStorage.removeItem('portail_eta_client');
    navigate('/portail-eta/login');
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatMessages((m) => [...m, { role: 'user', text: msg }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const r = await axios.post(
        `${API}/portail-eta/ai/chat`,
        { message: msg, session_id: `portail-eta-${client?.id}` },
        auth
      );
      setChatMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: r.data.reply,
          refused: r.data.refused,
          valentine_update: r.data.valentine_update,
          proactive: !!r.data.valentine_question,
        },
      ]);
      if (r.data.mba_teaser) setTeaser(r.data.mba_teaser);
    } catch (err) {
      setChatMessages((m) => [
        ...m,
        { role: 'assistant', text: 'Désolé, une erreur est survenue.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const submitDemande = async (form) => {
    try {
      await axios.post(`${API}/portail-eta/demandes-intervention`, form, auth);
      toast.success('Demande envoyée — votre ETA va vous recontacter');
      setDemandeForm(null);
      // refresh demandes
      const r = await axios.get(`${API}/portail-eta/demandes-intervention`, auth);
      setDemandes(r.data.demandes || []);
      setTab('demandes');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer la demande");
    }
  };

  const updateParcelle = async (parcelleId, changes) => {
    try {
      await axios.patch(`${API}/portail-eta/parcelles/${parcelleId}`, changes, auth);
      toast.success('Modification envoyée à votre ETA');
      const r = await axios.get(`${API}/portail-eta/parcelles`, auth);
      setParcelles(r.data.parcelles || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Modification impossible');
    }
  };

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'parcelles', label: 'Mes parcelles', icon: MapPin },
    { id: 'interventions', label: 'Interventions', icon: Calendar },
    { id: 'demandes', label: 'Mes demandes', icon: Plus },
    { id: 'photos', label: 'Mes photos', icon: Camera },
    { id: 'documents', label: 'Mes documents', icon: FileText, badge: documentsASigner },
    { id: 'bons', label: 'Bons & Factures', icon: FileText },
    { id: 'produits', label: 'Produits utilisés', icon: Package },
    { id: 'catalogue', label: "Catalogue ETA", icon: Sparkles },
    { id: 'chat', label: 'Assistant IA', icon: MessageSquare, badge: valentinePendingCount },
    { id: 'logiciel', label: 'Mon logiciel agricole', icon: Sprout },
  ];

  return (
    <div
      className="min-h-screen flex bg-slate-950 text-white"
      data-testid="portail-eta-page"
    >
      {/* SIDEBAR */}
      <aside className="w-72 border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-emerald-600 flex items-center justify-center">
              <Sprout className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <div className="font-bold text-sm">Espace client</div>
              <div className="text-xs text-emerald-200/70">
                {client.eta_nom || 'ETA'}
              </div>
            </div>
          </div>
          <div className="text-xs text-white/50">Connecté en tant que</div>
          <div className="text-sm font-medium truncate">
            {client.entreprise || client.nom}
          </div>
          <div className="text-xs text-white/40 truncate">{client.email}</div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`portail-eta-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                  tab === t.id
                    ? 'bg-gradient-to-r from-lime-500/20 to-emerald-500/20 border border-lime-400/30 text-lime-100'
                    : 'text-white/70 hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{t.label}</span>
                {t.badge > 0 && (
                  <span
                    data-testid="valentine-pending-badge"
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                  >
                    {t.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Pub Mon Bureau Agricole + CASIA */}
        <div className="p-4 space-y-3 border-t border-white/10">
          <a
            href="/bureau-agricole"
            target="_blank"
            rel="noreferrer"
            data-testid="portail-eta-pub-mba"
            className="block rounded-xl p-4 bg-gradient-to-br from-amber-400/10 to-orange-500/10 border border-amber-300/20 hover:border-amber-300/50 transition-all"
          >
            <div className="text-xs font-bold text-amber-300 mb-1">
              ✨ Mon Bureau Agricole
            </div>
            <div className="text-xs text-white/70 leading-relaxed">
              Conçu par des agriculteurs, pour les agriculteurs. Découvrez
              l'ERP qui s'adapte à votre ferme.
            </div>
          </a>
          <a
            href="/bureau-agricole#casia"
            target="_blank"
            rel="noreferrer"
            data-testid="portail-eta-pub-casia"
            className="block rounded-xl p-4 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-300/20 hover:border-fuchsia-300/50 transition-all"
          >
            <div className="text-xs font-bold text-fuchsia-300 mb-1">
              🧪 CASIA
            </div>
            <div className="text-xs text-white/70 leading-relaxed">
              L'IA agronomique du bureau agricole — bio-intrants nouvelle génération, soutenue par la Région Sud.
            </div>
          </a>
        </div>

        <button
          data-testid="portail-eta-logout"
          onClick={logout}
          className="m-4 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-red-500/20 text-sm text-white/70 transition-all"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </button>
      </aside>

      {/* MAIN */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : (
            <>
              {tab === 'stats' && <StatsView stats={stats} onAskIntervention={() => setDemandeForm({})} />}
              {tab === 'parcelles' && (
                <ParcellesView
                  parcelles={parcelles}
                  onAskIntervention={(p) => setDemandeForm({ parcelle_id: p.id, parcelle_nom: p.nom })}
                  onUpdateParcelle={updateParcelle}
                />
              )}
              {tab === 'interventions' && (
                <InterventionsView interventions={interventions} />
              )}
              {tab === 'demandes' && (
                <DemandesView demandes={demandes} onNew={() => setDemandeForm({})} />
              )}
              {tab === 'bons' && <BonsView bons={bons} />}
              {tab === 'documents' && (
                <DocumentsView
                  documents={documentsTous.documents || []}
                  stats={documentsTous.stats || {}}
                  onRefresh={fetchDocumentsTous}
                  token={token}
                  clientNom={client?.entreprise || client?.nom}
                />
              )}
              {tab === 'produits' && <ProduitsView produits={produitsUtilises} />}
              {tab === 'catalogue' && <CatalogueView catalogue={catalogue} />}
              {tab === 'chat' && (
                <ChatView
                  messages={chatMessages}
                  input={chatInput}
                  setInput={setChatInput}
                  onSend={sendChat}
                  loading={chatLoading}
                  teaser={teaser}
                  chatEndRef={chatEndRef}
                />
              )}
              {tab === 'logiciel' && <LogicielView clientNom={client?.entreprise || client?.nom} />}
            </>
          )}
        </div>
      </main>

      {demandeForm !== null && (
        <DemandeModal
          form={demandeForm}
          setForm={setDemandeForm}
          onCancel={() => setDemandeForm(null)}
          onSubmit={submitDemande}
          catalogue={catalogue}
          parcelles={parcelles}
        />
      )}
    </div>
  );
}

// ==================== TAB COMPONENTS ====================
function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl bg-white/5 border border-white/10 p-6 ${className}`}
    >
      {children}
    </div>
  );
}

function StatsView({ stats, onAskIntervention }) {
  if (!stats) return null;
  return (
    <div className="space-y-6" data-testid="portail-eta-stats">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold mb-1">Tableau de bord</h2>
          <p className="text-white/60 text-sm">
            Synthèse des interventions réalisées chez vous.
          </p>
        </div>
        <button
          onClick={onAskIntervention}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-emerald-950 font-bold shadow-lg shadow-lime-500/20 hover:brightness-110 transition-all"
          data-testid="btn-demander-intervention"
        >
          <Plus className="w-4 h-4" />
          Demander une intervention
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Parcelles" value={stats.nb_parcelles} icon={MapPin} />
        <Kpi
          label="Surface totale"
          value={`${stats.surface_totale_ha} ha`}
          icon={TrendingUp}
        />
        <Kpi
          label="Heures travaillées"
          value={stats.total_heures}
          icon={Calendar}
        />
        <Kpi
          label="Interventions"
          value={stats.nb_interventions}
          icon={FileText}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-semibold mb-4">Interventions par mois</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.interventions_par_mois || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="mois" stroke="#94a3b8" style={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: '#0f172a',
                    border: '1px solid #ffffff20',
                    borderRadius: 8,
                  }}
                />
                <Bar dataKey="nb" fill="#84cc16" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <h3 className="font-semibold mb-4">Répartition par type de chantier</h3>
          <div className="h-80 flex flex-col" data-testid="repartition-types-chart">
            <ResponsiveContainer width="100%" height="65%">
              <PieChart>
                <Pie
                  data={stats.repartition_types || []}
                  dataKey="nb"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  innerRadius={45}
                  paddingAngle={2}
                  stroke="#0f172a"
                  strokeWidth={2}
                  labelLine={false}
                  label={({ percent }) => percent > 0.06 ? `${Math.round(percent * 100)}%` : ''}
                >
                  {(stats.repartition_types || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#0f172a',
                    border: '1px solid #ffffff33',
                    borderRadius: 8,
                    color: '#fff',
                  }}
                  formatter={(value, name) => [`${value} chantier${value > 1 ? 's' : ''}`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Légende custom lisible — liste type + couleur + compte */}
            <div className="mt-3 grid grid-cols-2 gap-2 px-2 overflow-y-auto" data-testid="repartition-types-legend">
              {(stats.repartition_types || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ background: COLORS[i % COLORS.length] }}
                  />
                  <span className="truncate" title={t.type}>{t.type || 'Non précisé'}</span>
                  <span className="ml-auto font-semibold text-white/60">{t.nb}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card className="bg-gradient-to-br from-lime-500/10 to-emerald-500/10 border-lime-400/30">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-lime-400 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-50/90">
            <strong className="text-lime-300">Espace sécurisé — lecture seule.</strong>{' '}
            Vous ne pouvez pas créer ni modifier d'interventions depuis cet
            espace. Pour toute nouvelle demande, contactez directement votre
            ETA par téléphone ou email.
          </div>
        </div>
      </Card>

      {/* Pub rotative Mon Bureau Agricole + lead parrainage ETA */}
      <MBAPubCarousel />
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-4 h-4 text-lime-400" />
        <div className="text-xs text-white/60">{label}</div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </Card>
  );
}

function ParcellesView({ parcelles, onAskIntervention, onUpdateParcelle }) {
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ nom: '', variete: '' });
  const [viewMode, setViewMode] = useState('carte'); // 'carte' | 'liste'

  const startEdit = (p) => {
    setEditing(p.id);
    setEditForm({ nom: p.nom_parcelle || p.nom || '', variete: p.variete || '' });
  };
  const saveEdit = (p) => {
    const changes = {};
    if (editForm.nom && editForm.nom !== (p.nom_parcelle || p.nom)) changes.nom = editForm.nom;
    if (editForm.variete !== (p.variete || '')) changes.variete = editForm.variete;
    if (Object.keys(changes).length === 0) {
      setEditing(null);
      return;
    }
    onUpdateParcelle(p.id, changes);
    setEditing(null);
  };

  return (
    <div data-testid="portail-eta-parcelles">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold">Mes parcelles</h2>
          <p className="text-white/60 text-sm mt-1">
            {parcelles.length} parcelle{parcelles.length > 1 ? 's' : ''} suivie{parcelles.length > 1 ? 's' : ''} par votre ETA. Cliquez pour modifier le nom/variété ou demander une intervention.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
          <button
            onClick={() => setViewMode('carte')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${viewMode === 'carte' ? 'bg-emerald-500/30 text-emerald-200' : 'text-white/60 hover:text-white'}`}
            data-testid="btn-view-carte"
          >
            <MapIcon className="w-3.5 h-3.5" /> Carte
          </button>
          <button
            onClick={() => setViewMode('liste')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${viewMode === 'liste' ? 'bg-emerald-500/30 text-emerald-200' : 'text-white/60 hover:text-white'}`}
            data-testid="btn-view-liste"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Liste
          </button>
        </div>
      </div>

      {viewMode === 'carte' && (
        <div className="mb-6">
          <ParcellesMap
            parcelles={parcelles}
            onParcelleClick={() => {}}
            onAskIntervention={onAskIntervention}
          />
          <div className="mt-3 flex items-center gap-4 text-xs text-white/50 flex-wrap">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#a855f7' }}></span> Vignes</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#84cc16' }}></span> Oliviers</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#10b981' }}></span> Autre culture</span>
            <span className="ml-auto italic">Cliquez un point pour voir les détails et demander une intervention.</span>
          </div>
        </div>
      )}

      {viewMode === 'liste' && (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {parcelles.map((p) => {
          const isEditing = editing === p.id;
          const nom = p.nom_parcelle || p.nom || p.ilot || 'Parcelle sans nom';
          const surface = p.surface_ha || p.surface || 0;
          const culture = p.culture || p.culture_actuelle;
          const variete = p.variete;
          const isBio = p.conduite_bio || p.mode_conduite === 'biologique';
          return (
            <Card key={p.id} className="hover:border-lime-400/40 transition-all">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <MapPin className="w-4 h-4 text-lime-400 shrink-0" />
                  {isEditing ? (
                    <input
                      className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-white"
                      value={editForm.nom}
                      onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                    />
                  ) : (
                    <div className="font-semibold truncate">{nom}</div>
                  )}
                </div>
                {isBio && !isEditing && (
                  <span className="px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/30 text-[10px] font-bold text-emerald-300 uppercase tracking-wider shrink-0">
                    BIO
                  </span>
                )}
              </div>
              <div className="text-sm text-white/70 space-y-1">
                <div>Surface : <strong className="text-white">{surface} ha</strong></div>
                {culture && <div>Culture : <strong className="text-white/90">{culture}</strong></div>}
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <span>Variété :</span>
                    <input
                      className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-0.5 text-xs text-lime-200"
                      value={editForm.variete}
                      onChange={(e) => setEditForm({ ...editForm, variete: e.target.value })}
                      placeholder="ex: Viognier B, Marselan N"
                    />
                  </div>
                ) : (
                  variete && <div>Variété : <span className="text-lime-200/90">{variete}</span></div>
                )}
                {p.commune && <div className="text-xs text-white/50 mt-2">📍 {p.commune}</div>}
                {p.type_sol && <div className="text-xs text-white/50">Sol : {p.type_sol}</div>}
              </div>

              <div className="mt-4 flex items-center gap-2 pt-3 border-t border-white/10">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => saveEdit(p)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/30"
                      data-testid={`save-parcelle-${p.id}`}
                    >
                      <Check className="w-3 h-3" /> Valider
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-xs hover:bg-white/10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onAskIntervention(p)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-lime-400/15 border border-lime-400/30 text-lime-300 text-xs font-semibold hover:bg-lime-400/25"
                    >
                      <Plus className="w-3 h-3" /> Demander
                    </button>
                    <button
                      onClick={() => startEdit(p)}
                      className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-xs hover:bg-white/10"
                      title="Modifier nom / variété"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
        {parcelles.length === 0 && (
          <div className="text-white/50 text-sm">
            Aucune parcelle rattachée pour l'instant.
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function DemandesView({ demandes, onNew }) {
  const statutColor = {
    nouvelle: 'amber',
    vue: 'blue',
    planifiee: 'emerald',
    refusee: 'red',
    realisee: 'lime',
  };
  const statutLabel = {
    nouvelle: 'En attente',
    vue: 'Vue par l\'ETA',
    planifiee: 'Planifiée',
    refusee: 'Refusée',
    realisee: 'Réalisée',
  };
  return (
    <div data-testid="portail-eta-demandes">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold">Mes demandes d'intervention</h2>
          <p className="text-white/60 text-sm mt-1">
            Suivez l'avancement de vos demandes envoyées à l'ETA.
          </p>
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-emerald-950 font-bold shadow-lg shadow-lime-500/20 hover:brightness-110 transition-all"
          data-testid="btn-new-demande"
        >
          <Plus className="w-4 h-4" /> Nouvelle demande
        </button>
      </div>

      <div className="space-y-3">
        {demandes.map((d) => {
          const c = statutColor[d.statut] || 'slate';
          return (
            <Card key={d.id} className={`hover:border-${c}-400/40 transition-all`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`px-2 py-0.5 rounded-md bg-${c}-500/15 border border-${c}-400/30 text-[10px] font-bold text-${c}-300 uppercase tracking-wider`}>
                      {statutLabel[d.statut] || d.statut}
                    </span>
                    {d.urgence === 'haute' && (
                      <span className="px-2 py-0.5 rounded-md bg-red-500/15 border border-red-400/30 text-[10px] font-bold text-red-300 uppercase tracking-wider">
                        Urgent
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-base">{d.type_prestation}</div>
                  <div className="text-sm text-white/60 mt-0.5">
                    {d.parcelle_nom || 'Toutes parcelles'}
                    {d.surface_concernee_ha ? ` • ${d.surface_concernee_ha} ha` : ''}
                    {d.cible ? ` • ${d.cible}` : ''}
                  </div>
                  {d.commentaire && (
                    <div className="text-sm text-white/50 mt-2 italic">"{d.commentaire}"</div>
                  )}
                </div>
                <div className="text-right text-xs text-white/60 shrink-0">
                  <div className="font-semibold text-white/80">
                    {d.date_souhaitee ? `📅 ${d.date_souhaitee}` : 'ASAP'}
                  </div>
                  <div className="text-white/40 mt-0.5">
                    Envoyée le {(d.created_at || '').slice(0, 10)}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
        {demandes.length === 0 && (
          <div className="text-white/50 text-sm">
            Aucune demande pour l'instant. Cliquez sur « Nouvelle demande » ou demandez directement depuis une parcelle.
          </div>
        )}
      </div>
    </div>
  );
}

function DemandeModal({ form, setForm, onCancel, onSubmit, catalogue, parcelles }) {
  const [local, setLocal] = useState({
    type_prestation: form?.type_prestation || (catalogue[0]?.nom || 'Traitement phytosanitaire'),
    parcelle_id: form?.parcelle_id || '',
    parcelle_nom: form?.parcelle_nom || '',
    date_souhaitee: form?.date_souhaitee || '',
    urgence: form?.urgence || 'normale',
    cible: form?.cible || '',
    surface_concernee_ha: form?.surface_concernee_ha || '',
    commentaire: form?.commentaire || '',
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...local };
    if (payload.surface_concernee_ha === '' || payload.surface_concernee_ha === null) {
      delete payload.surface_concernee_ha;
    } else {
      payload.surface_concernee_ha = parseFloat(payload.surface_concernee_ha);
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="demande-modal">
      <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-emerald-900 to-lime-900 px-6 py-4 flex items-center justify-between border-b border-white/10">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Plus className="w-5 h-5 text-lime-400" />
            Nouvelle demande d'intervention
          </h2>
          <button onClick={onCancel} className="p-1 rounded hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 text-white">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Prestation souhaitée</label>
            <select
              required
              value={local.type_prestation}
              onChange={(e) => setLocal({ ...local, type_prestation: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
              data-testid="demande-prestation"
            >
              {catalogue.map((c, i) => (
                <option key={i} value={c.nom} className="bg-slate-900">{c.nom}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Parcelle concernée</label>
            <select
              value={local.parcelle_id || ''}
              onChange={(e) => {
                const p = parcelles.find((x) => x.id === e.target.value);
                setLocal({
                  ...local,
                  parcelle_id: e.target.value,
                  parcelle_nom: p ? (p.nom_parcelle || p.nom || '') : '',
                  surface_concernee_ha: p ? (p.surface_ha || p.surface || '') : local.surface_concernee_ha,
                });
              }}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
              data-testid="demande-parcelle"
            >
              <option value="" className="bg-slate-900">Toutes / non précisée</option>
              {parcelles.map((p) => (
                <option key={p.id} value={p.id} className="bg-slate-900">
                  {p.nom_parcelle || p.nom} ({p.surface_ha || 0} ha — {p.culture || 'parcelle'})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Date souhaitée</label>
              <input
                type="date"
                value={local.date_souhaitee}
                onChange={(e) => setLocal({ ...local, date_souhaitee: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                data-testid="demande-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Urgence</label>
              <select
                value={local.urgence}
                onChange={(e) => setLocal({ ...local, urgence: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                data-testid="demande-urgence"
              >
                <option value="basse" className="bg-slate-900">Basse</option>
                <option value="normale" className="bg-slate-900">Normale</option>
                <option value="haute" className="bg-slate-900">Haute (sous 48h)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Cible / objectif</label>
              <input
                type="text"
                placeholder="Mildiou, oïdium, mauvaises herbes..."
                value={local.cible}
                onChange={(e) => setLocal({ ...local, cible: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Surface (ha)</label>
              <input
                type="number"
                step="0.01"
                value={local.surface_concernee_ha}
                onChange={(e) => setLocal({ ...local, surface_concernee_ha: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-1 block">Commentaire</label>
            <textarea
              rows={3}
              value={local.commentaire}
              onChange={(e) => setLocal({ ...local, commentaire: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
              placeholder="Détails utiles : doses imposées, contraintes météo, voisins sensibles, etc."
              data-testid="demande-commentaire"
            />
          </div>

          <div className="flex gap-2 pt-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-emerald-950 font-bold hover:brightness-110"
              data-testid="demande-submit"
            >
              Envoyer la demande
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



function InterventionsView({ interventions }) {
  // Catégorisation par type
  const typeColor = (i) => {
    const op = (i.operation || i.type_intervention || i.type_activite || '').toLowerCase();
    const cat = (i.categorie_operation || '').toLowerCase();
    if (op.includes('phyto') || op.includes('traitement') || cat.includes('protection')) return { color: 'amber', label: 'Phyto' };
    if (op.includes('argile')) return { color: 'amber', label: 'Phyto BIO' };
    if (op.includes('broy') || op.includes('épareus') || op.includes('travail') || op.includes('labour') || op.includes('rotovator')) return { color: 'orange', label: 'Mécanique' };
    if (op.includes('conseil') || op.includes('audit') || op.includes('suivi')) return { color: 'cyan', label: 'Conseil' };
    if (op.includes('plantation') || op.includes('arrachage')) return { color: 'fuchsia', label: 'Plantation' };
    if (op.includes('récolte') || op.includes('vendange')) return { color: 'lime', label: 'Récolte' };
    return { color: 'slate', label: 'Autre' };
  };

  return (
    <div data-testid="portail-eta-interventions">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold">Historique des interventions</h2>
          <p className="text-white/60 text-sm mt-1">
            {interventions.length} intervention{interventions.length > 1 ? 's' : ''} réalisée{interventions.length > 1 ? 's' : ''} par votre ETA
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {interventions.map((i, idx) => {
          const t = typeColor(i);
          const op = i.type_intervention || i.operation || i.type_activite || 'Intervention';
          const parcelle = i.parcelle_nom || i.parcelle || i.culture || '';
          const surface = i.surface_travaillee || i.surface_ha;
          const isBio = i.mode_culture === 'BIO' || i.cahier_des_charges?.toLowerCase().includes('ab');
          const materiels = i.materiels || [];
          const operateurs = (i.main_oeuvre || []).map(m => m.nom || m).filter(Boolean);
          const produits = i.produits || [];
          return (
            <Card key={idx} className={`hover:border-${t.color}-400/40 transition-all`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`px-2 py-0.5 rounded-md bg-${t.color}-500/15 border border-${t.color}-400/30 text-[10px] font-bold text-${t.color}-300 uppercase tracking-wider`}>
                      {t.label}
                    </span>
                    {isBio && (
                      <span className="px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/30 text-[10px] font-bold text-emerald-300 uppercase tracking-wider">
                        BIO
                      </span>
                    )}
                    {i.cible && (
                      <span className="text-xs text-white/50">→ {i.cible}</span>
                    )}
                  </div>
                  <div className="font-semibold text-base">{op}</div>
                  <div className="text-sm text-white/60 mt-0.5">
                    {parcelle}
                    {surface ? ` • ${surface} ha` : ''}
                    {i.stade ? ` • Stade ${i.stade}` : ''}
                  </div>
                  {operateurs.length > 0 && (
                    <div className="text-xs text-white/50 mt-2">
                      👤 {operateurs.join(' + ')}
                    </div>
                  )}
                  {materiels.length > 0 && (
                    <div className="text-xs text-white/50 mt-1">
                      🚜 {materiels.slice(0, 3).join(' · ')}
                      {materiels.length > 3 ? ` +${materiels.length - 3}` : ''}
                    </div>
                  )}
                  {produits.length > 0 && (
                    <div className="text-xs text-amber-200/70 mt-1">
                      🧪 {produits.slice(0, 4).join(' · ')}
                    </div>
                  )}
                  {(i.observations || i.notes) && (
                    <div className="text-sm text-white/50 mt-2 italic">
                      {i.observations || i.notes}
                    </div>
                  )}
                </div>
                <div className="text-right text-xs text-white/60 shrink-0">
                  <div className="font-semibold text-white/80">
                    {(i.date || i.date_debut || '').slice(0, 10)}
                  </div>
                  {(i.heure_debut || i.heure_fin) && (
                    <div className="text-white/50 mt-0.5">
                      {i.heure_debut} → {i.heure_fin}
                    </div>
                  )}
                  {(i.duree_facturable_heures || i.duree_heures) && (
                    <div className="mt-1">
                      ⏱ {i.duree_facturable_heures || i.duree_heures}h
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {interventions.length === 0 && (
          <div className="text-white/50 text-sm">Aucune intervention enregistrée.</div>
        )}
      </div>
    </div>
  );
}

function PhotosView({ photos }) {
  return (
    <div data-testid="portail-eta-photos">
      <h2 className="text-3xl font-bold mb-6">Photos de vos parcelles</h2>
      {photos.length === 0 ? (
        <div className="text-white/50 text-sm">
          Aucune photo disponible pour l'instant. Les photos prises par l'équipe
          de l'ETA lors des interventions apparaîtront ici automatiquement.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((ph, idx) => {
            const src = ph.url.startsWith('http')
              ? ph.url
              : `${process.env.REACT_APP_BACKEND_URL}${ph.url}`;
            return (
              <a
                key={idx}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-xl overflow-hidden border border-white/10 hover:border-lime-400/50 transition-all bg-white/5"
              >
                <div className="aspect-square overflow-hidden bg-slate-800">
                  <img
                    src={src}
                    alt={ph.legende || ph.parcelle || 'Photo'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="p-3">
                  <div className="text-xs font-semibold truncate">
                    {ph.parcelle || ph.legende || 'Photo'}
                  </div>
                  <div className="text-xs text-white/50 mt-1 truncate">
                    {ph.operation}
                    {ph.date ? ` • ${String(ph.date).slice(0, 10)}` : ''}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}


function DocumentsView({ documents, stats, onRefresh, token, clientNom }) {
  const [filter, setFilter] = useState('all');
  const [signing, setSigning] = useState(null); // doc en cours de signature

  const typeLabel = {
    contrat: 'Contrat',
    bon_chantier: 'Bon de chantier',
    attestation_bio: 'Attestation BIO',
    facture: 'Facture',
  };
  const typeIcon = {
    contrat: Pen,
    bon_chantier: FileText,
    attestation_bio: CheckCircle,
    facture: Euro,
  };
  const typeColor = {
    contrat: 'from-amber-500/20 to-orange-500/10 border-amber-400/30 text-amber-100',
    bon_chantier: 'from-sky-500/20 to-blue-500/10 border-sky-400/30 text-sky-100',
    attestation_bio: 'from-emerald-500/20 to-green-500/10 border-emerald-400/30 text-emerald-100',
    facture: 'from-violet-500/20 to-fuchsia-500/10 border-violet-400/30 text-violet-100',
  };

  const filtered = filter === 'all' ? documents : filter === 'a_signer' ? documents.filter(d => d.action === 'signer') : documents.filter(d => d.type === filter);

  const handleSignContrat = async ({ nom, qualite, data_url }) => {
    const r = await fetch(`${API}/portail-eta/contrats-prestation/signer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        contrat_id: signing.id,
        signature_data_url: data_url,
        signataire_nom: qualite ? `${nom} (${qualite})` : nom,
      }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || 'Erreur signature');
    }
    toast.success('Contrat signé ! Votre ETA a été notifiée.');
    setSigning(null);
    onRefresh?.();
  };

  return (
    <div className="space-y-6" data-testid="documents-view">
      {/* Stats du haut */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button onClick={() => setFilter('all')}
          className={`p-4 rounded-xl border transition-all ${filter === 'all' ? 'bg-white/10 border-white/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
          <div className="text-2xl font-bold">{stats.total || 0}</div>
          <div className="text-xs text-white/60">Total documents</div>
        </button>
        <button onClick={() => setFilter('a_signer')}
          className={`p-4 rounded-xl border transition-all ${filter === 'a_signer' ? 'bg-amber-500/20 border-amber-400/50' : 'bg-amber-500/5 border-amber-400/20 hover:bg-amber-500/10'}`}
          data-testid="filter-a-signer">
          <div className="text-2xl font-bold text-amber-300">{stats.a_signer || 0}</div>
          <div className="text-xs text-amber-200/70">À signer</div>
        </button>
        <button onClick={() => setFilter('contrat')}
          className={`p-4 rounded-xl border transition-all ${filter === 'contrat' ? 'bg-white/10 border-white/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
          <div className="text-2xl font-bold">{stats.contrats || 0}</div>
          <div className="text-xs text-white/60">Contrats</div>
        </button>
        <button onClick={() => setFilter('attestation_bio')}
          className={`p-4 rounded-xl border transition-all ${filter === 'attestation_bio' ? 'bg-emerald-500/20 border-emerald-400/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
          <div className="text-2xl font-bold text-emerald-300">{stats.attestations_bio || 0}</div>
          <div className="text-xs text-white/60">Attestations BIO</div>
        </button>
        <button onClick={() => setFilter('facture')}
          className={`p-4 rounded-xl border transition-all ${filter === 'facture' ? 'bg-white/10 border-white/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
          <div className="text-2xl font-bold">{stats.factures || 0}</div>
          <div className="text-xs text-white/60">Factures</div>
        </button>
      </div>

      {/* Liste des documents */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun document {filter !== 'all' && 'dans cette catégorie'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((doc) => {
            const Icon = typeIcon[doc.type] || FileText;
            return (
              <div key={doc.id}
                className={`p-4 rounded-xl bg-gradient-to-br ${typeColor[doc.type] || 'from-white/5 to-white/0 border-white/10'} border transition-all hover:scale-[1.01]`}
                data-testid={`doc-card-${doc.type}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wide font-bold opacity-60">{typeLabel[doc.type] || doc.type}</span>
                      {doc.cdc && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200">{doc.cdc}</span>}
                    </div>
                    <div className="text-sm font-semibold mb-1 truncate" title={doc.titre}>{doc.titre}</div>
                    <div className="text-xs text-white/50 flex items-center gap-3">
                      <span>{doc.date || '—'}</span>
                      {doc.montant_ht > 0 && <span>• {doc.montant_ht} € HT</span>}
                      {doc.montant_ttc > 0 && <span>• {doc.montant_ttc} € TTC</span>}
                      {doc.signe && <span className="text-emerald-300 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> signé</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {doc.url_pdf && (
                      <a href={`${API.replace('/api', '')}${doc.url_pdf}?t=${token}`}
                        target="_blank" rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-white/10 text-xs hover:bg-white/20 inline-flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Voir
                      </a>
                    )}
                    {doc.action === 'signer' && doc.type === 'contrat' && (
                      <button onClick={() => setSigning(doc)}
                        className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-xs font-bold hover:bg-amber-400 inline-flex items-center gap-1"
                        data-testid={`btn-sign-contrat-${doc.id}`}>
                        <Pen className="w-3 h-3" /> Signer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SignaturePadModal
        open={!!signing}
        onClose={() => setSigning(null)}
        onSign={handleSignContrat}
        title="Signer votre contrat"
        subtitle={signing?.titre || ''}
        defaultNom={clientNom || ''}
      />
    </div>
  );
}


function BonsView({ bons }) {
  return (
    <div data-testid="portail-eta-bons">
      <h2 className="text-3xl font-bold mb-6">Bons de livraison & Factures</h2>
      <div className="space-y-3">
        {bons.map((b, idx) => (
          <Card key={idx}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                      b._type === 'facturation'
                        ? 'bg-amber-400/20 text-amber-300'
                        : 'bg-cyan-400/20 text-cyan-300'
                    }`}
                  >
                    {b._type === 'facturation' ? 'Facture' : 'Bon livraison'}
                  </span>
                  <span className="font-semibold">
                    {b.numero_facture || b.numero || b.libelle || 'Document'}
                  </span>
                </div>
                <div className="text-sm text-white/60 mt-1">{b.libelle}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-white/60">{(b.date || '').slice(0, 10)}</div>
                {(b.montant_ttc || b.montant_ht) && (
                  <div className="text-sm font-semibold mt-1">
                    {(b.montant_ttc || b.montant_ht).toLocaleString('fr-FR')} €
                  </div>
                )}
                {b.statut_paiement && (
                  <div className="text-xs text-white/50">{b.statut_paiement}</div>
                )}
              </div>
            </div>
          </Card>
        ))}
        {bons.length === 0 && (
          <div className="text-white/50 text-sm">Aucun bon ou facture.</div>
        )}
      </div>
    </div>
  );
}

function ProduitsView({ produits }) {
  return (
    <div data-testid="portail-eta-produits">
      <h2 className="text-3xl font-bold mb-6">Produits utilisés sur vos parcelles</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {produits.map((p, idx) => (
          <Card key={idx}>
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-emerald-400" />
              <div className="font-semibold">{p.nom}</div>
            </div>
            <div className="text-sm text-white/70">
              Quantité totale : <strong>{p.quantite_totale.toFixed(2)} {p.unite}</strong>
            </div>
            <div className="text-xs text-white/50 mt-1">
              {p.nb_utilisations} utilisation(s)
            </div>
          </Card>
        ))}
        {produits.length === 0 && (
          <div className="text-white/50 text-sm col-span-2">
            Aucun produit tracé pour le moment.
          </div>
        )}
      </div>
    </div>
  );
}

function CatalogueView({ catalogue }) {
  // Group by categorie
  const grouped = catalogue.reduce((acc, c) => {
    const k = c.categorie || 'Travaux';
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});
  return (
    <div data-testid="portail-eta-catalogue">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold">Nos prestations</h2>
          <p className="text-white/60 text-sm mt-1">
            Toutes les prestations que nous pouvons réaliser pour vous. Demandez-nous un devis pour le tarif adapté à votre exploitation.
          </p>
        </div>
      </div>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="mb-8">
          <h3 className="text-xs uppercase tracking-[0.25em] text-lime-300/80 font-bold mb-3">
            {cat}
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((c, idx) => (
              <Card key={idx} className="hover:border-lime-400/40 transition-all">
                <div className="font-semibold mb-1">{c.nom}</div>
                {c.description && (
                  <div className="text-xs text-white/60 leading-relaxed">{c.description}</div>
                )}
              </Card>
            ))}
          </div>
        </div>
      ))}
      {catalogue.length === 0 && (
        <div className="text-white/50 text-sm">
          Le catalogue n'est pas encore publié par votre ETA.
        </div>
      )}
    </div>
  );
}

function LogicielView({ clientNom }) {
  const features = [
    { icon: '🗺️', titre: 'Parcellaire & cartographie', desc: 'Toutes vos parcelles dessinées sur carte satellite, avec variétés, surfaces, historique des cultures, rotations.' },
    { icon: '🍇', titre: 'Cave & vinification', desc: 'Stocks vins, embouteillage, étiquettes, traçabilité cuvée, déclarations DRM mensuelles auto sur Prodouane.' },
    { icon: '🏪', titre: 'Caveau de caisse', desc: 'Ventes B2C en direct au caveau, encaissement carte/espèces/chèque, ticket électronique, comptabilité auto.' },
    { icon: '📋', titre: 'CVI & déclarations viti', desc: 'Casier Viticole Informatisé tenu à jour automatiquement (replantation, arrachage, surgreffage, conversion AB).' },
    { icon: '🚚', titre: 'Lettre de voiture & traçabilité', desc: 'Émission auto des lettres de voiture (vrac, conditionné, vendange, olive), registre des sorties propre pour les douanes.' },
    { icon: '🌿', titre: 'Bio / HVE / IGP', desc: 'Suivi cahier des charges Ecocert / Bureau Veritas, fiches préparation matériel auto, attestations Gérant, audit blanc avant contrôle.' },
    { icon: '💰', titre: 'Facturation Chorus Pro', desc: 'Devis, BL, factures avec signature électronique eIDAS, dépôt automatique Chorus Pro pour les marchés publics.' },
    { icon: '🤖', titre: 'Agent IA vocal', desc: "Vous parlez (français), il fait. Plus rapide qu'un salarié pour saisir une intervention, retrouver une fiche, préparer un devis." },
    { icon: '👥', titre: 'Portail clients restaurateurs', desc: 'Vos cavistes et restaurateurs commandent depuis leur portail dédié, suivent les expéditions, signent les BL.' },
    { icon: '📊', titre: 'Tableau de bord domaine', desc: 'Surfaces, rendements, coûts/ha, marges par cuvée, prévisionnel récolte, comparaison millésimes.' },
  ];

  return (
    <div data-testid="portail-eta-logiciel">
      <div className="bg-gradient-to-br from-emerald-900/40 via-emerald-800/30 to-lime-900/40 border border-emerald-400/20 rounded-3xl p-8 mb-8">
        <div className="inline-block px-3 py-1 rounded-full bg-lime-400/20 border border-lime-400/30 text-lime-300 text-[10px] font-bold uppercase tracking-[0.25em] mb-4">
          Pour {clientNom || 'votre domaine'}
        </div>
        <h2 className="text-3xl md:text-4xl font-black mb-3 text-transparent bg-clip-text bg-gradient-to-r from-lime-300 via-emerald-300 to-teal-300">
          Mon Bureau Agricole
        </h2>
        <p className="text-emerald-100/80 text-base md:text-lg leading-relaxed max-w-3xl">
          L'ERP qui pilote toute votre exploitation viticole — du parcellaire à la cave, de la commande au caveau jusqu'à la facture Chorus Pro. <strong>Plus aucune ressaisie.</strong>
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-8">
        {features.map((f, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-lime-400/40 transition-all">
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="font-bold text-base mb-1">{f.titre}</div>
            <div className="text-sm text-white/60 leading-relaxed">{f.desc}</div>
          </div>
        ))}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
        <h3 className="text-xs uppercase tracking-[0.25em] text-lime-300/80 font-bold mb-4">Tarifs modulables</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border border-white/10 rounded-xl p-4">
            <div className="text-sm text-white/60 mb-1">Petite exploitation</div>
            <div className="text-2xl font-black">50€<span className="text-sm font-normal text-white/50"> / mois</span></div>
            <div className="text-xs text-white/50 mt-1">Jusqu'à 20 ha</div>
          </div>
          <div className="border-2 border-lime-400/50 rounded-xl p-4 bg-lime-400/5">
            <div className="text-[10px] font-bold text-lime-300 uppercase tracking-wider mb-1">Recommandé</div>
            <div className="text-sm text-white/70 mb-1">Domaine + cave</div>
            <div className="text-2xl font-black text-lime-300">500€<span className="text-sm font-normal text-white/50"> / mois</span></div>
            <div className="text-xs text-white/60 mt-1">Cave, caveau, B2C, restaurateurs</div>
          </div>
          <div className="border border-white/10 rounded-xl p-4">
            <div className="text-sm text-white/60 mb-1">Multi-structures</div>
            <div className="text-2xl font-black">1200€<span className="text-sm font-normal text-white/50"> / mois</span></div>
            <div className="text-xs text-white/50 mt-1">Plusieurs sociétés</div>
          </div>
        </div>
        <p className="text-xs text-white/50 mt-4 italic">
          Tarifs adaptables. On commence léger, on grossit avec vous. Vous évoluez, on bascule sans douleur.
        </p>
      </div>

      <div className="bg-gradient-to-r from-fuchsia-900/30 to-violet-900/30 border border-fuchsia-400/20 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-fuchsia-300">CASIA</span>
          <span className="px-2 py-0.5 rounded bg-fuchsia-400/20 border border-fuchsia-400/30 text-[10px] font-bold text-fuchsia-300">Région Sud</span>
        </div>
        <h3 className="text-xl font-bold mb-2 text-fuchsia-200">L'IA agronomique pour les bio-intrants nouvelle génération</h3>
        <p className="text-white/70 text-sm leading-relaxed mb-3">
          Marque pionnière soutenue par la Région Sud Provence-Alpes-Côte d'Azur. CASIA accompagne déjà <strong>plus de 300 exploitations</strong> dans leur transition bio et bas-intrants. Bilan cuivre/soufre cumulé en temps réel, alertes plafond AB, recommandations agronomiques contextuelles.
        </p>
        <p className="text-xs text-fuchsia-300/70 italic">
          Conforme cahiers des charges AB Ecocert, Demeter, Nature & Progrès, HVE.
        </p>
      </div>

      <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-6 text-center">
        <div className="text-2xl mb-2">🎁</div>
        <h3 className="text-lg font-bold mb-2">Essai gratuit 30 jours</h3>
        <p className="text-white/70 text-sm mb-4 max-w-xl mx-auto">
          Découvrez Mon Bureau Agricole sans engagement. On reprend vos données existantes (Geofolia, factures, parcellaire), on configure votre cave, et on vous accompagne en visio jusqu'à la prise en main.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            href="mailto:monbureauagricole@gmail.com?subject=Je%20veux%20essayer%20Mon%20Bureau%20Agricole&body=Bonjour%2C%0A%0AJ%27aimerais%20essayer%20gratuitement%20Mon%20Bureau%20Agricole%20pour%20mon%20domaine.%0A%0AMerci%20de%20me%20rappeler.%0A%0ANom%20%3A%0AT%C3%A9l%C3%A9phone%20%3A%0ASurface%20totale%20%3A%0AAvec%20cave%20%3A%20oui%20%2F%20non"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-emerald-950 font-bold hover:brightness-110 transition-all"
            data-testid="cta-essayer-mba"
          >
            ✉️ Demander mon essai
          </a>
          <a
            href="/bureau-agricole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition-all"
          >
            En savoir plus →
          </a>
        </div>
      </div>
    </div>
  );
}



function ChatView({ messages, input, setInput, onSend, loading, teaser, chatEndRef }) {
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="portail-eta-chat">
      <h2 className="text-3xl font-bold mb-2">Assistant IA</h2>
      <p className="text-white/60 text-sm mb-4">
        Posez vos questions sur vos parcelles, interventions, produits. Lecture seule.
      </p>

      <div className="flex-1 overflow-y-auto space-y-3 pr-2">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                m.role === 'user'
                  ? 'bg-gradient-to-br from-lime-500 to-emerald-600 text-slate-900 font-medium'
                  : m.refused
                    ? 'bg-amber-500/10 border border-amber-400/30 text-amber-100'
                    : m.proactive
                      ? 'bg-sky-500/10 border border-sky-400/40 text-sky-50'
                      : m.valentine_update
                        ? 'bg-emerald-500/10 border border-emerald-400/40 text-emerald-50'
                        : 'bg-white/5 border border-white/10 text-white/90'
              }`}
              data-testid={m.proactive ? 'valentine-proactive-question' : (m.valentine_update ? 'valentine-update-confirmation' : 'assistant-message')}
            >
              {m.proactive && (
                <div className="text-xs text-sky-300 font-bold mb-1 uppercase tracking-wide">
                  🤖 Valentine — question pour compléter votre dossier
                </div>
              )}
              {m.valentine_update && (
                <div className="text-xs text-emerald-300 font-bold mb-1 uppercase tracking-wide">
                  ✅ Merci — dossier mis à jour
                </div>
              )}
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> L'assistant réfléchit...
          </div>
        )}
        {teaser && (
          <a
            href={teaser.url}
            target="_blank"
            rel="noreferrer"
            className="block mt-4 p-4 rounded-2xl bg-gradient-to-br from-amber-400/15 to-orange-500/15 border border-amber-300/30 hover:border-amber-300/60 transition-all"
          >
            <div className="text-xs text-amber-300 font-bold mb-1">
              {teaser.marque === 'CASIA' ? '🧪' : '✨'} {teaser.marque}
            </div>
            <div className="font-semibold mb-1">{teaser.titre}</div>
            <div className="text-sm text-white/70">{teaser.sous_titre}</div>
            <div className="text-xs text-amber-200 mt-2">{teaser.cta} →</div>
          </a>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="mt-4 flex gap-2">
        <input
          data-testid="portail-eta-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSend()}
          placeholder="Ex : Combien d'heures sur la parcelle Nord en mars ?"
          className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-lime-400/60"
        />
        <button
          data-testid="portail-eta-chat-send"
          onClick={onSend}
          disabled={loading || !input.trim()}
          className="px-5 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-slate-900 font-semibold disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
